import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Image as ImageIcon, Plus, Star, Trash2, Undo2, X } from "lucide-react";
import {
  MAX_IMAGE_BYTES,
  MAX_PRODUCT_IMAGES,
  checkImageSize,
  isUnlimited,
  sniffImageType,
} from "@marketing/shared";
import { apiErrorMessage } from "@/lib/api";
import { useCurrentWorkspace } from "@/lib/queries";
import {
  useAddProductImage,
  useCreateProduct,
  useDeleteProduct,
  useDeleteProductImage,
  useRevertProductImage,
  useSetPrimaryImage,
  useProducts,
  useUpdateProduct,
  type Product,
} from "@/lib/catalogue";
import { Notice, SkeletonRows, Spinner } from "@/components/ui";

// =============================================================================
// Listing the products a shop actually sells, with photographs of them.
//
// Why this screen exists, in the owner's terms: generated pictures used to
// show a product that was not theirs — a generic sack of rice, a fish that was
// not the fish in their freezer. Photograph what you sell and the pictures
// show the real thing.
//
// This section saves on its own, unlike the rest of the brand voice screen,
// which batches everything behind one Save. A photograph cannot wait for a
// form submit: uploads are slow, they fail on their own terms, and a Save that
// sometimes means "upload four files" is a Save nobody trusts. Each action
// here is complete the moment it reports success.
// =============================================================================

export function ProductCatalogue({ brandId }: { brandId: string | null }) {
  const { t } = useTranslation();
  const products = useProducts(brandId);
  const workspace = useCurrentWorkspace();
  const [error, setError] = useState<string | null>(null);

  const cap = workspace.data?.limits.maxProductsPerBrand ?? 0;
  const used = products.data?.length ?? 0;
  const atCap = cap > 0 && used >= cap && !isUnlimited(cap);

  if (!brandId) return null;

  return (
    <div className="space-y-4">
      <p className="text-xs leading-relaxed text-muted">{t("catalogue.why")}</p>

      {error ? (
        <Notice tone="warning">{error}</Notice>
      ) : null}

      {products.isLoading ? (
        <SkeletonRows rows={2} />
      ) : used === 0 ? (
        <div className="rounded-xl border border-dashed border-border px-4 py-6 text-center">
          <ImageIcon size={20} className="mx-auto mb-2 text-muted" />
          <p className="text-sm font-medium text-ink">{t("catalogue.empty")}</p>
          <p className="mt-1 text-xs text-muted">{t("catalogue.emptyHint")}</p>
        </div>
      ) : (
        <ul className="space-y-3">
          {products.data!.map((product) => (
            <ProductRow key={product.id} product={product} onError={setError} />
          ))}
        </ul>
      )}

      <div className="flex items-center justify-between gap-3 text-xs text-muted">
        <span className="tabular-nums">
          {isUnlimited(cap)
            ? t("catalogue.countUnlimited", { used })
            : t("catalogue.count", { used, total: cap })}
        </span>
      </div>

      {atCap ? (
        <Notice tone="info">{t("catalogue.capReached", { total: cap })}</Notice>
      ) : (
        <AddProduct brandId={brandId} onError={setError} />
      )}
    </div>
  );
}

/**
 * The same verdict the server will reach, reached here first.
 *
 * Size from the File's own metadata and type from its first 16 bytes, so a
 * 40 MB photo or a PDF is refused without reading or uploading the whole
 * thing. The server repeats both — this saves a wasted upload, it is not
 * trusted.
 */
async function rejectReason(file: File): Promise<string | null> {
  const size = checkImageSize(file.size);
  if (size) return size.reason;
  const head = new Uint8Array(await file.slice(0, 16).arrayBuffer());
  return sniffImageType(head) ? null : "unsupported_type";
}

/** One file picker, with the local check and the translated refusal. */
function usePickPhoto(onError: (message: string) => void) {
  const { t } = useTranslation();
  return async function pick(file: File | null): Promise<File | null> {
    if (!file) return null;
    const reason = await rejectReason(file);
    if (!reason) return file;
    onError(
      t(`photo.reject.${reason}`, {
        mb: (file.size / (1024 * 1024)).toFixed(1),
        limit: MAX_IMAGE_BYTES / (1024 * 1024),
      }),
    );
    return null;
  };
}

function AddProduct({
  brandId,
  onError,
}: {
  brandId: string;
  onError: (message: string | null) => void;
}) {
  const { t } = useTranslation();
  const create = useCreateProduct();
  const pick = usePickPhoto(onError);

  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [price, setPrice] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [tidy, setTidy] = useState(true);
  const input = useRef<HTMLInputElement>(null);

  function reset() {
    setName("");
    setDescription("");
    setPrice("");
    setFile(null);
    setTidy(true);
    if (input.current) input.current.value = "";
  }

  async function submit() {
    if (!name.trim() || !file) return;
    onError(null);
    try {
      await create.mutateAsync({
        brandId,
        name: name.trim(),
        description: description.trim() || undefined,
        price: price.trim() || undefined,
        tidy,
        file,
      });
      reset();
      setOpen(false);
    } catch (err) {
      onError(apiErrorMessage(err, t("errors.generic")));
    }
  }

  if (!open) {
    return (
      <button type="button" onClick={() => setOpen(true)} className="btn-secondary w-full">
        <Plus size={15} />
        {t("catalogue.addTitle")}
      </button>
    );
  }

  return (
    <div className="space-y-3 rounded-xl border border-border bg-surface p-4">
      <label className="block">
        <span className="label">{t("catalogue.name")}</span>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={t("catalogue.namePlaceholder")}
          maxLength={120}
          className="input"
          autoFocus
        />
      </label>

      <label className="block">
        <span className="label">{t("catalogue.photo")}</span>
        <input
          ref={input}
          type="file"
          accept="image/png,image/jpeg,image/webp"
          onChange={(e) =>
            void pick(e.target.files?.[0] ?? null).then((f) => {
              setFile(f);
              if (!f && input.current) input.current.value = "";
            })
          }
          className="input file:me-3 file:rounded-md file:border-0 file:bg-primary-50 file:px-3 file:py-1.5 file:text-primary-700"
        />
        <span className="hint">{t("catalogue.photoHint")}</span>
      </label>

      <label className="flex items-start gap-2.5">
        <input
          type="checkbox"
          checked={tidy}
          onChange={(e) => setTidy(e.target.checked)}
          className="mt-0.5"
        />
        <span>
          <span className="block text-sm text-ink">{t("catalogue.tidyLabel")}</span>
          <span className="hint">{t("catalogue.tidyHint")}</span>
        </span>
      </label>

      <label className="block">
        <span className="label">{t("catalogue.description")}</span>
        <input
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder={t("catalogue.descriptionPlaceholder")}
          maxLength={600}
          className="input"
        />
        <span className="hint">{t("catalogue.descriptionHint")}</span>
      </label>

      <label className="block">
        <span className="label">{t("catalogue.price")}</span>
        <input
          value={price}
          onChange={(e) => setPrice(e.target.value)}
          placeholder={t("catalogue.pricePlaceholder")}
          maxLength={80}
          className="input"
        />
        <span className="hint">{t("catalogue.priceHint")}</span>
      </label>

      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => void submit()}
          disabled={!name.trim() || !file || create.isPending}
          className="btn-primary flex-1"
        >
          {create.isPending ? <Spinner /> : <Plus size={15} />}
          {create.isPending ? t("catalogue.adding") : t("catalogue.add")}
        </button>
        <button
          type="button"
          onClick={() => {
            reset();
            setOpen(false);
          }}
          className="btn-secondary"
        >
          {t("common.cancel")}
        </button>
      </div>
    </div>
  );
}

function ProductRow({
  product,
  onError,
}: {
  product: Product;
  onError: (message: string | null) => void;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const primary = product.images.find((i) => i.isPrimary) ?? product.images[0];

  return (
    <li className="rounded-xl border border-border">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="flex w-full items-center gap-3 p-3 text-start transition-colors hover:bg-surface"
      >
        {primary ? (
          <img
            src={primary.url}
            alt=""
            className="h-12 w-12 shrink-0 rounded-lg object-cover"
            loading="lazy"
          />
        ) : (
          <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-surface text-muted">
            <ImageIcon size={16} />
          </span>
        )}
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-medium text-ink">{product.name}</span>
          <span className="block truncate text-xs text-muted">
            {product.price ? product.price : t("catalogue.noPrice")}
            {product.images.length > 1
              ? ` · ${t("catalogue.photoCount", { count: product.images.length })}`
              : ""}
          </span>
        </span>
        {!product.isActive ? (
          <span className="shrink-0 rounded-full bg-surface px-2 py-0.5 text-[10px] font-medium text-muted">
            {t("catalogue.outOfStock")}
          </span>
        ) : null}
      </button>

      {open ? <ProductEditor product={product} onError={onError} /> : null}
    </li>
  );
}

function ProductEditor({
  product,
  onError,
}: {
  product: Product;
  onError: (message: string | null) => void;
}) {
  const { t } = useTranslation();
  const update = useUpdateProduct();
  const remove = useDeleteProduct();
  const addImage = useAddProductImage();
  const setPrimary = useSetPrimaryImage();
  const removeImage = useDeleteProductImage();
  const revertImage = useRevertProductImage();
  const pick = usePickPhoto(onError);

  const [name, setName] = useState(product.name);
  const [description, setDescription] = useState(product.description ?? "");
  const [price, setPrice] = useState(product.price ?? "");
  const [confirming, setConfirming] = useState(false);
  const input = useRef<HTMLInputElement>(null);

  const dirty =
    name !== product.name ||
    description !== (product.description ?? "") ||
    price !== (product.price ?? "");

  async function run(action: () => Promise<unknown>) {
    onError(null);
    try {
      await action();
    } catch (err) {
      onError(apiErrorMessage(err, t("errors.generic")));
    }
  }

  return (
    <div className="space-y-4 border-t border-border p-4">
      {/* ── The photographs ── */}
      <div>
        <span className="label">{t("catalogue.photos")}</span>
        <ul className="mt-1 flex flex-wrap gap-2">
          {product.images.map((image) => (
            <li key={image.id} className="relative">
              <img
                src={image.url}
                alt=""
                className={`h-16 w-16 rounded-lg object-cover ${
                  image.isPrimary ? "ring-2 ring-primary-600" : ""
                }`}
                loading="lazy"
              />
              {image.isPrimary ? (
                <span className="absolute -top-1 -start-1 rounded-full bg-primary-600 p-1 text-white">
                  <Star size={9} />
                </span>
              ) : (
                <button
                  type="button"
                  title={t("catalogue.setMain")}
                  aria-label={t("catalogue.setMain")}
                  onClick={() =>
                    void run(() =>
                      setPrimary.mutateAsync({ productId: product.id, imageId: image.id }),
                    )
                  }
                  className="absolute -top-1 -start-1 rounded-full bg-surface p-1 text-muted shadow ring-1 ring-border hover:text-ink"
                >
                  <Star size={9} />
                </button>
              )}
              {image.sourceUrl ? (
                <button
                  type="button"
                  title={t("catalogue.revertPhoto")}
                  aria-label={t("catalogue.revertPhoto")}
                  onClick={() =>
                    void run(() =>
                      revertImage.mutateAsync({ productId: product.id, imageId: image.id }),
                    )
                  }
                  className="absolute -bottom-1 -start-1 rounded-full bg-surface p-1 text-muted shadow ring-1 ring-border hover:text-ink"
                >
                  <Undo2 size={9} />
                </button>
              ) : null}
              {product.images.length > 1 ? (
                <button
                  type="button"
                  title={t("catalogue.removePhoto")}
                  aria-label={t("catalogue.removePhoto")}
                  onClick={() =>
                    void run(() =>
                      removeImage.mutateAsync({ productId: product.id, imageId: image.id }),
                    )
                  }
                  className="absolute -top-1 -end-1 rounded-full bg-surface p-1 text-muted shadow ring-1 ring-border hover:text-danger"
                >
                  <X size={9} />
                </button>
              ) : null}
            </li>
          ))}

          {product.images.length < MAX_PRODUCT_IMAGES ? (
            <li>
              <button
                type="button"
                onClick={() => input.current?.click()}
                disabled={addImage.isPending}
                title={t("catalogue.addPhoto")}
                aria-label={t("catalogue.addPhoto")}
                className="flex h-16 w-16 items-center justify-center rounded-lg border border-dashed border-border text-muted hover:text-ink"
              >
                {addImage.isPending ? <Spinner /> : <Plus size={16} />}
              </button>
              <input
                ref={input}
                type="file"
                accept="image/png,image/jpeg,image/webp"
                className="hidden"
                onChange={(e) =>
                  void pick(e.target.files?.[0] ?? null).then((file) => {
                    if (input.current) input.current.value = "";
                    if (file) {
                      void run(() => addImage.mutateAsync({ productId: product.id, file }));
                    }
                  })
                }
              />
            </li>
          ) : null}
        </ul>
        <span className="hint">
          {t("catalogue.photosHint", { max: MAX_PRODUCT_IMAGES })}
          {product.images.some((i) => i.sourceUrl)
            ? ` ${t("catalogue.revertHint")}`
            : ""}
        </span>
      </div>

      {/* ── The words ── */}
      <label className="block">
        <span className="label">{t("catalogue.name")}</span>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={120}
          className="input"
        />
      </label>

      <label className="block">
        <span className="label">{t("catalogue.description")}</span>
        <input
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          maxLength={600}
          className="input"
        />
      </label>

      <label className="block">
        <span className="label">{t("catalogue.price")}</span>
        <input
          value={price}
          onChange={(e) => setPrice(e.target.value)}
          maxLength={80}
          className="input"
        />
        <span className="hint">{t("catalogue.priceHint")}</span>
      </label>

      {/* ── Out of stock, which is not the same as deleted ── */}
      <label className="flex items-start gap-2.5">
        <input
          type="checkbox"
          checked={!product.isActive}
          onChange={(e) =>
            void run(() =>
              update.mutateAsync({ id: product.id, isActive: !e.target.checked }),
            )
          }
          className="mt-0.5"
        />
        <span>
          <span className="block text-sm text-ink">{t("catalogue.outOfStockLabel")}</span>
          <span className="hint">{t("catalogue.outOfStockHint")}</span>
        </span>
      </label>

      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          disabled={!dirty || update.isPending}
          onClick={() =>
            void run(() =>
              update.mutateAsync({
                id: product.id,
                name: name.trim(),
                description: description.trim() || null,
                price: price.trim() || null,
              }),
            )
          }
          className="btn-primary"
        >
          {update.isPending ? <Spinner /> : null}
          {update.isPending ? t("catalogue.saving") : t("catalogue.save")}
        </button>

        {confirming ? (
          <>
            <button
              type="button"
              onClick={() => void run(() => remove.mutateAsync(product.id))}
              disabled={remove.isPending}
              className="btn-secondary text-danger"
            >
              {remove.isPending ? <Spinner /> : <Trash2 size={15} />}
              {t("catalogue.removeConfirm")}
            </button>
            <button type="button" onClick={() => setConfirming(false)} className="btn-secondary">
              {t("common.cancel")}
            </button>
          </>
        ) : (
          <button
            type="button"
            onClick={() => setConfirming(true)}
            className="btn-secondary text-muted"
          >
            <Trash2 size={15} />
            {t("catalogue.remove")}
          </button>
        )}
      </div>

      <p className="text-[11px] text-muted">
        {product.lastFeaturedAt
          ? t("catalogue.lastFeatured", {
              date: new Date(product.lastFeaturedAt).toLocaleDateString(),
            })
          : t("catalogue.neverFeatured")}
      </p>
    </div>
  );
}
